import React from "react";
import { useDispatch, useSelector } from "react-redux";
import localization from "../../data/config";
import { track } from "../../actions/dtm";
import { PART_META, PART_NUMBER } from "../../constants/config/parameters";
import { PROTOCOL, HOST, LANGUAGE } from "../../constants";
import { determineFilters } from "../../utilities/filters";
import { EXCEL_FILENAME } from "../../constants/config/tools";
import * as EVENTS from "../../constants/config/dtm";
import downloadIcon from "../../img/icons/downloadIcon.svg";

function ExcelDownload(props) {
  const dispatch = useDispatch();

  const { data, parameters, title, labels } =
    useSelector((state) => ({
      data: state.parts.filteredData,
      parameters: state.filters.parameters,
      title: state.app.title,
      compareState: state.filters.comparing,
      selected: state.parts.selected,
      labels: state.labels,
    }));

  /**
   * @name createRequest
   * @function
   * @description Create the request object to have the excel file created
   */
  const createRequest = () => {
    const UNFORMATTED_FIELD = "value",
      FORMATTED_FIELD = "displayValue";

    let now = new Date(),
      columns = formatColumns(),
      filters = formatFilters(),
      data = {
        formattedDataRows: [
          columns.labelsTemp,
          columns.UOM,
          ...formatData(columns.parametersTemp, FORMATTED_FIELD),
        ],
        rawDataRows: [
          columns.labelsTemp,
          columns.UOM,
          ...formatData(columns.parametersTemp, UNFORMATTED_FIELD),
        ],
        generics: formatData([PART_META], FORMATTED_FIELD).reduce(
          (a, b) => [...a, ...b],
          []
        ),
        baseUrl: `${PROTOCOL}//${HOST}/${LANGUAGE}/`,
        coverSheetData: {
          nameValueData: {
            [labels.excel.dateCreated]: now.toLocaleString(),
            [labels.excel.productType]: title,
            [labels.excel.webLink]: processURL(window.location.toString()),
          },
          filterConditions: [[labels.excel.filter.conditions]],
        },
        messages: {
          cover_sheet_name: labels.excel.cover,
          web_display_sheet_name: labels.excel.display.web,
          raw_data_sheet_name: labels.excel.display.raw,
        },
      };

    if (filters.length > 0) {
      data.coverSheetData.filterConditions[0].push(
        labels.excel.columnName,
        labels.excel.filter.values
      );
      data.coverSheetData.filterConditions.push(...filters);
    } else {
      data.coverSheetData.filterConditions[0].push(
        `(${labels.excel.filter.none})`
      );
    }

    sendRequest(escape(JSON.stringify(data)));
  };

  /**
   * @name formatColumns
   * @function
   * @description Build column headers for the excel sheet
   */
  const formatColumns = () => {
    let labelsTemp = [labels.partNumber],
      UOM = [""],
      parametersTemp = [PART_META];
    parameters.forEach((parameter) => {
      if(parameter.id !== undefined){
        labelsTemp.push(
          `${parameter.name}${parameter.delay ? ` (${parameter.delay})` : ""}`
        );
        UOM.push(parameter.uom || "");
        parametersTemp.push(parameter.id);
      }
    });

    return { labelsTemp, UOM, parametersTemp };
  };

  /**
   * @name processURL
   * @function
   * @description Formats the url for excel
   */


  const processURL = (urlString) => {
    urlString = urlString.replace( /(\|)+/g, '|');
    return urlString;

  }

  /**
   * @name formatData
   * @function
   * @description Formats the app data into an array of arrays
   * @param {array} parameters Parameters that should be displayed
   * @param {string} key Which parameter value should be returned inside the array
   */
  const formatData = (parameters, key) =>
    data.map((part) => {
      let ary = [];
      if(parameters){
        parameters.forEach((parameter) => {
          let p = part[parameter],
            value = p ? part[parameter][key] : null;
          ary.push(Array.isArray(value) ? value.join(" ") : value);
        });
      }     
      return ary;
    });

  /**
   * @name formatFilters
   * @description Creates a human readable array of the applied filters
   * @returns {array} array of the filters, where the first item is the column name
   */
  const formatFilters = () => {
    let filters = determineFilters().filters,
      rows = [],
      filterData = function (filters, id) {
        return filters[id]
          .map((filter) => (filter.value === "" ? "Blank" : filter.value))
          .join(", ");
      };

    if (filters && filters.length > 0) {
      filters = filters.reduce((a, b) => ({ ...a, ...b }), {});

      Object.keys(filters).forEach((id) => {
        let parameter = parameters.find((parameter) => parameter.id === id);
        if (parameter || id === PART_META)
          rows.push([
            "",
            id === PART_META ? PART_NUMBER : parameter.name,
            filterData(filters, id),
          ]);
      });
    }

    return rows;
  };

  /**
   * @name request
   * @param {string} data Stringified JSON object of data presented in the table
   * @description Sends request to the endpoint for an excel file
   */
  const sendRequest = (data) => {
    // track through DTM
    dispatch(track(EVENTS.DOWNLOAD_EXCEL));

    let form = document.createElement("form"),
      fileName = document.createElement("input"),
      dataInput = document.createElement("input");

    fileName.type = "hidden";
    dataInput.type = "hidden";

    form.appendChild(fileName);
    form.appendChild(dataInput);
    form.action = EXCEL_FILENAME;
    form.method = "POST";

    dataInput.name = "data";
    dataInput.value = data;
    fileName.name = "fileName";
    fileName.value = `${labels.excel.filename}.xlsx`;
    document.body.appendChild(form);
    form.onsubmit = (e) => form.remove();
    form.submit();
  };

  /**
  * Handles the key press event, triggering an action if Enter key is pressed.
  *
  * @param {KeyboardEvent} event - The key press event.
  */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      createRequest();
    }
  };

  return (
    <div tabIndex={0} role="button" aria-label={localization.download} onKeyDown={handleKeyDown}>
      <img src={downloadIcon} alt="excelDownload" onClick={createRequest} />
    </div>
  );
}

export default ExcelDownload;
